import React from "react";
import styled from "styled-components";

const Document = styled.img`
  display: none;
  height: 70px;
  width: fit-content;
  background-color: #000;
  border-radius: 10px;
  &:hover {
    cursor: pointer;
    opacity: 0.8;
  }
`;

const Description = styled.div`
  width: 100%;
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => theme.text_primary};
  opacity: 0.9;
  line-height: 1.7;
  margin-top: 16px;
  margin-bottom: 12px;
  
  ul {
    padding-left: 0;
    list-style: none;
    
    li {
      margin-bottom: 8px;
      position: relative;
      padding-left: 20px;
      
      &::before {
        content: '\u25b8';
        position: absolute;
        left: 0;
        color: #FF6B6B;
        font-weight: 600;
      }
    }
  }
  
  @media only screen and (max-width: 768px) {
    font-size: 13px;
    line-height: 1.6;
  }
`;

const Span = styled.span`
  overflow: hidden;
  display: -webkit-box;
  max-width: 100%;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  
  &:hover {
    -webkit-line-clamp: unset;
    overflow: visible;
  }
`;

const Card = styled.div`
  width: 650px;
  border-radius: 16px;
  background: linear-gradient(135deg, ${({ theme }) => theme.card} 0%, ${({ theme }) => theme.card_light} 100%);
  border: 1px solid rgba(133, 76, 230, 0.3);
  box-shadow: 0 8px 32px rgba(133, 76, 230, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 20px 24px;
  justify-content: space-between;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 16px;
  transition: all 0.3s ease;
  will-change: transform, box-shadow;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #FF6B6B, #ff8a8a, #ffb3b3);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    box-shadow: 0 16px 48px rgba(133, 76, 230, 0.3), 0 4px 16px rgba(0, 0, 0, 0.15);
    transform: translateY(-6px);
    border-color: rgba(133, 76, 230, 0.5);
    
    &::before {
      opacity: 1;
    }
  }
  
  @media only screen and (max-width: 768px) {
    padding: 16px 20px;
    gap: 12px;
    width: 320px;
  }

  &:hover ${Document} {
    display: flex;
  }

  &:hover ${Span} {
    overflow: visible;
    -webkit-line-clamp: unset;
  }
`;

const Top = styled.div`
  width: 100%;
  display: flex;
  gap: 12px;
`;

const Image = styled.img`
  height: 60px;
  width: 60px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(133, 76, 230, 0.05));
  border-radius: 12px;
  padding: 6px;
  object-fit: contain;
  object-position: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(133, 76, 230, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &:hover {
    transform: scale(1.08) translateY(-2px);
    box-shadow: 0 8px 32px rgba(133, 76, 230, 0.2), 0 4px 16px rgba(0, 0, 0, 0.15);
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(133, 76, 230, 0.1));
    border-color: rgba(133, 76, 230, 0.2);
  }
  
  @media only screen and (max-width: 768px) {
    height: 50px;
    width: 50px;
    padding: 5px;
  }
`;

const Body = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  gap: 12px;
`;

const LeftContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const Role = styled.div`
  font-size: 21px;
  font-weight: 700;
  color: ${({ theme }) => theme.text_primary};
  @media only screen and (max-width: 768px) {
    font-size: 17px;
  }
`;

const Company = styled.div`
  font-size: 17px;
  font-weight: 600;
  color: #854CE6;
  @media only screen and (max-width: 768px) {
    font-size: 15px;
  }
`;

const Date = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #854CE6;
  background: rgba(133, 76, 230, 0.08);
  padding: 4px 8px;
  border-radius: 8px;
  display: inline-block;
  margin-top: 0;
  border: 1px solid rgba(133, 76, 230, 0.15);
  width: fit-content;
  @media only screen and (max-width: 768px) {
    font-size: 11px;
    padding: 3px 6px;
  }
`;

const Skills = styled.div`
  width: 100%;
  display: flex;
  gap: 12px;
  margin-top: 20px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -16px;
    left: 50%;
    transform: translateX(-50%);
    width: 80%;
    max-width: 200px;
    height: 1px;
    background: linear-gradient(90deg, transparent, #FF6B6B, transparent);
    opacity: 0.8;
  }
`;

const ItemWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Skill = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.text_primary};
  background: rgba(133, 76, 230, 0.08);
  padding: 6px 12px;
  border-radius: 16px;
  border: 1px solid rgba(133, 76, 230, 0.2);
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(133, 76, 230, 0.15);
    transform: translateY(-1px);
  }
  
  @media only screen and (max-width: 768px) {
    font-size: 11px;
    padding: 4px 8px;
  }
`;

const ExperienceCard = ({ experience }) => {
  return (
    <Card>
      <Top>
        <Image src={experience.img} />
        <Body>
          <TopRow>
            <LeftContent>
              <Role>{experience.role}</Role>
              <Company>{experience.company}</Company>
            </LeftContent>
            <Date>{experience.date}</Date>
          </TopRow>
        </Body>
      </Top>
      <Description>
        {experience?.desc && (
          <>
            <Span>
              <ul>
                {experience.desc.map((point, index) => (
                  <li key={index}>{point}</li>
                ))}
              </ul>
            </Span>
          </>
        )}
        {experience?.skills && (
          <>
            <br />
            <Skills>
              <b>Skills:</b>
              <ItemWrapper>
                {experience?.skills?.map((skill, index) => (
                  <Skill>• {skill}</Skill>
                ))}
              </ItemWrapper>
            </Skills>
          </>
        )}
      </Description>
      {experience.doc && (
        <a href={experience.doc} target="new">
          <Document src={experience.doc} />
        </a>
      )}
    </Card>
  );
};

export default ExperienceCard;
